import Glide from '@glidejs/glide'

/*
 * Function to setup all instances of the block
 */
export async function init(rootSelector) {
	// Magic values
	const desktopThreshold = 1024;

	// Init handlers
	const fetcher = new Fetcher();

	// Fetch all markets
	const markets = await fetcher.fetchMarkets();

	// Function to render a case study card
	// ESLint isn't recognising that this var gets hoisted so just ignoring it
	// eslint-disable-next-line
	const renderTemplate = ({ post }) => renderTemplateInner({ post });

	// Create a controller for each block instance found in the DOM
	document.querySelectorAll(`.${rootSelector}`).forEach(async (rootEl) => {
		// Init handlers
		const mapHandler = new MapHandler();
		const glideHandler = new GlideHandler();

		// Fetch markets and posts
		const posts = await fetcher.fetchPosts({ rootEl });

		// Wrap the template render function
		const renderTemplate = ({ post }) =>
			renderTemplateInner({ markets, post, rootSelector });

		// Only init the map handler when the placeholder is visible at desktop resolution
		const mapEl = rootEl.querySelector(`.${rootSelector}__map-placeholder`);
		const mapObserver = new window.IntersectionObserver((entries) => {
			if (
				true === entries[0].isIntersecting &&
				desktopThreshold <= window.innerWidth
			) {
				mapObserver.unobserve(mapEl);
				mapHandler.init({ mapEl, posts, renderTemplate });
			}
		});
		mapObserver.observe(mapEl);

		// Init the Glide carousel
		const carouselEl = rootEl.querySelector(`.${rootSelector}__carousel`);
		glideHandler.init({
			rootSelector,
			carouselEl,
			posts,
			renderTemplate,
		});
	});
}

/*
 * Class to init and handle mobile Glide carousel
 */
class GlideHandler {
	/****/
	async init({
		rootSelector,
		carouselEl,
		posts,
		renderTemplate,
	}) {
		// Find the slides container
		const slidesEl = carouselEl.querySelector(
			`.glide__slides`
		);

		// Convert posts to slides and append them to the carousel
		const slideEls = posts.map((post) => renderTemplate({ post }));
		slideEls.forEach((o) => slidesEl.appendChild(o));
		this.slideCount = slideEls.length;

		// Init the carousel
		this.glideInstance = new Glide(carouselEl);

		// Listen for move events
		this.glideInstance.on('move.after', this.updatePagination.bind(this))

		// Mount the carousel instance
		this.glideInstance.mount();

		// Find arrows
		this.arrowSelector = `.${rootSelector}__carousel-arrow`;
		this.prevArrowSelector = `.${rootSelector}__carousel-arrow--prev`;
		this.nextArrowSelector = `.${rootSelector}__carousel-arrow--next`;
		this.prevArrowEl = carouselEl.querySelector(this.prevArrowSelector);
		this.nextArrowEl = carouselEl.querySelector(this.nextArrowSelector);

		// Store the slide count element
		this.slideCountEl = carouselEl.querySelector(
			`.${rootSelector}__carousel-slide-count`
		);

		// Listen for user clicking arrow buttons
		carouselEl.addEventListener('click', this.handleArrowClicks.bind(this));
	}

	/*
	 * Update the X / Y pagination text
	 */
	updatePagination() {
		// Concat the text
		const current = this.glideInstance.index + 1;
		const total = this.slideCount;
		const text = `${current} / ${total}`;

		// Replace current text with updated values
		this.slideCountEl.innerHTML = '';
		this.slideCountEl.appendChild(document.createTextNode(text));
	}

	/*
	 * Handle the user clicking a carousel arrow
	 *
	 * @param    {MouseEvent} event    An event raised by a user action
	 */
	handleArrowClicks(event) {
		// Must be clicking an arrow or a child element of an arrow
		if(
			!event.target.matches(this.arrowSelector)
			&& !event.target.matches(`${this.arrowSelector} *`)
		) {
			return;
		}

		//
		event.preventDefault();

		// Choose the action to take
		const arrowEl = event.target.closest(this.arrowSelector);
		if (arrowEl.matches(this.prevArrowSelector)) {
			this.glideInstance.go('<');
		} else if (arrowEl.matches(this.nextArrowSelector)) {
			this.glideInstance.go('>');
		}
	}
}

/*
 * Render the case study card
 *
 * @param     {Object[]} markets       A list of market data objects
 * @param     {post} post              The post to render
 * @param     {string} rootSelector    The identifying class of the block
 * @return    {DocumentFragment}
 */
function renderTemplateInner({ markets, post, rootSelector }) {
	// Collect elements
	const templateEl = document.querySelector(
		`.${rootSelector}__card-template`
	);
	const clone = templateEl.content.cloneNode(true);
	const innerEl = clone.querySelector('.map-card__inner');
	const thumbnailEl = clone.querySelector('.map-card__thumbnail');
	const imageEl = clone.querySelector('.map-card__thumbnail-image');
	const titleEl = clone.querySelector('.map-card__title');
	const excerptEl = clone.querySelector('.map-card__excerpt');
	const marketsEl = clone.querySelector('.map-card__markets');
	const locationEl = clone.querySelector('.map-card__location');

	// Update the card URL
	innerEl.setAttribute('href', post.link);

	// Update the card thumbnail
	if ('string' === typeof post.image && post.image.length > 0) {
		imageEl.setAttribute('src', post.image);
	} else {
		thumbnailEl.parentNode.removeChild(thumbnailEl);
	}

	// Update the title and excerpt
	titleEl.innerHTML = post.title;
	excerptEl.innerHTML = post.excerpt;

	// Add links to markets
	if (post.markets instanceof Array && 0 < post.markets.length) {
		// Fragment to stop elements
		const fragment = new window.DocumentFragment();

		// Create each link
		post.markets.forEach((marketId) => {
			// Find the market
			const market = markets.find((market) => market.id === marketId);
			if ('undefined' === typeof market) {
				return;
			}

			// Create elements
			const itemEl = document.createElement('li');
			const linkEl = document.createElement('a');

			// Set link values
			linkEl.setAttribute('href', market.link);
			linkEl.innerHTML = market.name;

			// Append items
			itemEl.appendChild(linkEl);
			fragment.appendChild(itemEl);
		});

		// Append the fragment to the DOM
		marketsEl.innerHTML = '';
		marketsEl.appendChild(fragment);
	} else {
		marketsEl.parentNode.removeChild(marketsEl);
	}

	// Update the location
	if ('string' === typeof post.location && post.location.length > 0) {
		locationEl.innerHTML = post.location;
	} else {
		locationEl.parentNode.removeChild(locationEl);
	}

	// Done
	return clone;
}

/*
 * Map handler
 */
class MapHandler {
	/*
	 * Return the URL of the default map pin
	 *
	 * @return    {string}
	 */
	get defaultPinUrl() {
		return '/wp-content/themes/brush/resources/images/pin-default.png';
	}

	/*
	 * Return the URL of the active map pin
	 *
	 * @return    {string}
	 */
	get activePinUrl() {
		return '/wp-content/themes/brush/resources/images/pin-active.png';
	}

	/*
	 * Init the block handler
	 *
	 * @param     {HTMLElement} mapEl          The element to replace with a maps instance
	 * @param     {Object[]} posts             An array of shaped post objects
	 * @param     {Function} renderTemplate    Function to render a case study card
	 * @return    {void}
	 */
	async init({ mapEl, posts, renderTemplate }) {
		// Abort if map is already initialised
		if (true === mapEl.dataset.initialised) {
			return;
		}
		mapEl.dataset.initialised = true;

		// Magic vars
		const defaultZoom = 4;

		// Make sure the maps API is available
		await this.pollMapsApi();

		// Create the map instance
		const initArgs = this.getMapInitArgs(defaultZoom);
		const map = this.createMap({ mapEl, initArgs });

		// Create markers
		const markers = this.createMarkers({ map, posts, renderTemplate });

		// Zoom and centre on markers
		this.centreMarkers({ map, markers });

		setTimeout(() => {
			map.setOptions({ maxZoom: 16 });
		}, 1000);
	}

	/*
	 * Wait for the Google Maps API to init
	 *
	 * @return    {Promise<void>}
	 */
	pollMapsApi() {
		// Return polling promise
		return new Promise((resolve) => {
			// Looping callback used to check condition
			(function callback() {
				// If API loaded
				if ('undefined' !== typeof window.google) {
					return resolve();
				}

				// Queue the next callback loop
				window.requestAnimationFrame(callback);
			})();
		});
	}

	/*
	 * Return an object used to configure a new map instance
	 *
	 * @param     {number} defaultZoom    The default zoom level once the map has loaded
	 * @return    {Object}
	 */
	getMapInitArgs(defaultZoom) {
		return {
			center: { lat: 51.5074, lng: 0.1278 },
			mapTypeControl: false,
			mapTypeId: window.google.maps.MapTypeId.ROADMAP,
			streetViewControl: false,
			// eslint-disable-next-line
			styles: [{"featureType":"all","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"all","elementType":"labels.text","stylers":[{"visibility":"off"}]},{"featureType":"all","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"administrative","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"administrative","elementType":"labels.text.fill","stylers":[{"color":"#444444"}]},{"featureType":"administrative.locality","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"administrative.neighborhood","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"administrative.land_parcel","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#0D4478"},{"visibility":"simplified"}]},{"featureType":"landscape","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"landscape.natural.landcover","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"landscape.natural.terrain","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"poi","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"all","stylers":[{"saturation":-100},{"lightness":45},{"visibility":"off"}]},{"featureType":"road","elementType":"geometry","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"labels.text","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"road.highway","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"road.arterial","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"labels.text","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"water","elementType":"all","stylers":[{"color":"#18232c"},{"visibility":"on"}]},{"featureType":"water","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"water","elementType":"labels.text","stylers":[{"visibility":"off"}]},{"featureType":"water","elementType":"labels.icon","stylers":[{"visibility":"off"}]}],
			zoom: defaultZoom,
			maxZoom: defaultZoom,
		};
	}

	/*
	 * Init a map instance
	 *
	 * @param     {HTMLElement} mapEl    The element to replace with a maps instance
	 * @param     {Object} initArgs      An object of arguments used to init the map
	 * @return    {Map}
	 */
	createMap({ mapEl, initArgs }) {
		return new window.google.maps.Map(mapEl, initArgs);
	}

	/*
	 * Create all markers for the map
	 *
	 * @param     {HTMLElement} mapEl          The element to replace with a maps instance
	 * @param     {Object[]} posts             An array of shaped post objects
	 * @param     {Function} renderTemplate    Function to render a case study card
	 * @return    {Object[]}
	 */
	createMarkers({ map, posts, renderTemplate }) {
		// Create the info window
		const infoWindow = new window.google.maps.InfoWindow({
			pixelOffset: new window.google.maps.Size(155, -32),
		});

		//
		const markers = posts.map((post) => {
			// Basic config
			const markerConfig = {
				icon: this.defaultPinUrl,
				map,
				position: {
					lat: parseFloat(post.latitude),
					lng: parseFloat(post.longitude),
				},
				title: post.title,
			};

			// Create the marker
			const marker = new window.google.maps.Marker(markerConfig);

			// Handle clicking the marker
			marker.addListener('click', () =>
				this.markerClicked({
					infoWindow,
					map,
					marker,
					markers,
					post,
					renderTemplate,
				})
			);

			// Done
			return marker;
		});

		//
		return markers;
	}

	/*
	 * A marker has been clicked
	 *
	 * @param     {InfoWindow} infoWindow      The info window instance
	 * @param     {Map} map                    The map instance
	 * @param     {Marker} marker              The clicked marker
	 * @param     {Marker[]} markers           An array of all markers
	 * @param     {Object} post                The post record associated with the marker
	 * @param     {Function} renderTemplate    Function to render a case study card
	 * @return    {void}
	 */
	markerClicked({ infoWindow, map, marker, markers, post, renderTemplate }) {
		// Reset all marker icons
		markers.forEach((o) => o.setIcon(this.defaultPinUrl));

		// Is the info window already open on this marker?
		const alreadyOpen = infoWindow.anchor === marker;

		// Info window needs to be closed before updating
		infoWindow.close();

		if (!alreadyOpen) {
			// Update the info window content
			const content = renderTemplate({ post });
			infoWindow.setContent(content);

			// Open the info window on the clicked marker
			infoWindow.open({
				anchor: marker,
				map,
			});

			// Set the active pin state
			marker.setIcon(this.activePinUrl);
		}
	}

	/*
	 * Centre the map on the markers supplied
	 *
	 * @param     {Map} map                    The map instance
	 * @param     {Marker[]} markers           An array of all markers
	 * @return    {void}
	 */
	centreMarkers({ map, markers }) {
		// Reposition and zoom the map to contain the markers
		switch (markers.length) {
			// Fail without markers
			case 0:
				return;

			// Explicitly focus lone markers
			case 1:
				map.setCenter(markers.find(() => true).getPosition());
				break;

			// Focus multiple markers
			default:
				const bounds = new window.google.maps.LatLngBounds();
				markers.forEach((o) => bounds.extend(o.getPosition()));
				map.fitBounds(bounds);
				map.setCenter(bounds.getCenter());
				break;
		}
	}
}

/*
 * Post fetcher
 */
class Fetcher {
	/*
	 * Fetch all markets from the REST API
	 *
	 * @return    {Promise<Object[]>}
	 */
	async fetchMarkets() {
		// Init a URL builder
		const endpointUrl = new URL(
			'/wp-json/wp/v2/markets',
			window.location.origin
		);

		// Pull data from the REST API
		const restResponse = await window
			.fetch(endpointUrl.toString())
			.then((response) => response.json());

		// Shape and return the data
		return restResponse.map(this.shapeMarketResponse, this);
	}

	/*
	 * Create a new data object from a REST API market response object
	 *
	 * @param     {Object} post    Raw data object returned by the REST API
	 * @return    {Object}
	 */
	shapeMarketResponse(market) {
		return {
			id: market.id,
			link: market.link,
			name: market.name,
		};
	}

	/*
	 * Fetch posts from the REST API using dataset values
	 *
	 * @param     {HTMLElement} rootEl    The root element of the block
	 * @return    {Promise<Object[]>}
	 */
	async fetchPosts({ rootEl }) {
		// Read values from the root els dataset
		const categoryId = parseInt(rootEl.dataset.categoryId, 10);
		const filterType = rootEl.dataset.filterType;

		// Init a URL builder
		const endpointUrl = new URL(
			'/wp-json/wp/v2/posts',
			window.location.origin
		);

		// Add basic query params
		endpointUrl.searchParams.set('per_page', 99);
		endpointUrl.searchParams.set('categories', categoryId);

		// Add manual filtering
		if ('manual' === filterType) {
			const manualFilters = this.getManualPostFilters({ rootEl });
			endpointUrl.searchParams.set('include', manualFilters);
		}

		// Add business unit filtering
		if ('business-units' === filterType) {
			const businessUnitFilter = parseInt(
				rootEl.dataset.businessUnitFilter,
				10
			);
			endpointUrl.searchParams.set('business-units', businessUnitFilter);
		}

		// Add market filtering
		if ('markets' === filterType) {
			const marketFilter = parseInt(rootEl.dataset.marketFilter, 10);
			endpointUrl.searchParams.set('markets', marketFilter);
		}

		// Pull data from the REST API
		const restResponse = await window
			.fetch(endpointUrl.toString())
			.then((response) => response.json());

		// Shape and return the data
		return restResponse.map(this.shapePostResponse, this);
	}

	/*
	 * Read manual filters (a list of post IDs) from the root els dataset
	 *
	 * @param     {HTMLElement} rootEl    The root element of the block
	 * @return    {number[]}
	 */
	getManualPostFilters({ rootEl }) {
		return (rootEl.dataset.manualFilters ?? '')
			.split(',')
			.filter((v) => !isNaN(v) && 0 < v.length)
			.map((v) => parseInt(v, 0));
	}

	/*
	 * Create a new data object from a REST API post response object
	 *
	 * @param     {Object} post    Raw data object returned by the REST API
	 * @return    {Object}
	 */
	shapePostResponse(post) {
		return {
			excerpt: post.excerpt.rendered,
			id: post.id,
			image: post.featured_image_src,
			latitude: post.latlng[0],
			link: post.link,
			location: post.location,
			longitude: post.latlng[1],
			markets: post.markets,
			title: post.title.rendered,
		};
	}
}
